import { useEffect, useRef, useState } from "react";

import backend from "../../../backend/backend";

export const POLL_INTERVAL_MS = 1500;

/**
 * How long the waiting screen keeps promising a result before it admits it doesn't know.
 *
 * Analysis normally takes 5-10 minutes, but nothing reports failure of the async job itself: a
 * backend restart mid-analysis leaves the playground in ANALYZING forever. Past this point the UI
 * offers a way out instead of spinning silently -- the caller decides what that looks like.
 */
export const ANALYSIS_TIMEOUT_MS = 15 * 60 * 1000;

const TERMINAL_STATUSES = ["ANALYZED", "ERROR", "IN_USE"];

/** Statuses that mean "this playground is not yours / not there", not "try again later". */
const GONE_HTTP_STATUSES = [400, 403, 404];

/**
 * Polls a playground until its analysis reaches a terminal status.
 *
 * @param uid playground to watch; null/undefined polls nothing
 * @param onFinished called once with the playground when it reaches ANALYZED / ERROR / IN_USE
 * @param onMissing called once when the playground is gone or not the caller's
 * @returns {{ timedOut: boolean }} true once the analysis has outlived {@link ANALYSIS_TIMEOUT_MS};
 *          polling continues, because a late answer is still worth having
 */
const usePlaygroundAnalysisPolling = (uid, { onFinished, onMissing } = {}) => {
  const [timedOut, setTimedOut] = useState(false);

  // Kept in refs so a caller that re-creates its callbacks every render (the common case) doesn't
  // restart the poll loop -- the effect must depend on the uid alone.
  const onFinishedRef = useRef(onFinished);
  const onMissingRef = useRef(onMissing);
  onFinishedRef.current = onFinished;
  onMissingRef.current = onMissing;

  useEffect(() => {
    if (!uid) return undefined;

    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    setTimedOut(false);

    const check = async () => {
      try {
        const playground = await backend.getPlayground(uid);
        if (cancelled) return;
        // A playground that was deleted or belongs to another team comes back as 200 with an empty
        // body: the repository lookup returns null and there is nothing to serialize.
        if (!playground || !playground.uid) {
          onMissingRef.current?.();
          return;
        }
        if (TERMINAL_STATUSES.includes(playground.status)) {
          onFinishedRef.current?.(playground);
          return;
        }
      } catch (e) {
        if (cancelled) return;
        // httpAPI rejects with the raw Response. A 401 has already logged the user out globally.
        if (GONE_HTTP_STATUSES.includes(e?.status)) {
          onMissingRef.current?.();
          return;
        }
        // Anything else (offline, 5xx, a deploy in progress) is worth retrying: the analysis on the
        // server is unaffected by our inability to ask about it.
        console.log("Error polling playground analysis status ", e);
      }
      if (cancelled) return;
      if (Date.now() - startedAt >= ANALYSIS_TIMEOUT_MS) {
        setTimedOut(true);
      }
      timer = setTimeout(check, POLL_INTERVAL_MS);
    };

    check();

    // Without this the recursive setTimeout outlives the component and keeps setting state on it.
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [uid]);

  return { timedOut };
};

export default usePlaygroundAnalysisPolling;
